/**
 * Session store — who is signed in, and the per-account theme override.
 *
 * The store holds no transport. `attach()` is called once from `main.ts` with an
 * `AuthClient`, and once from each test with a fake. That is deliberately
 * explicit rather than an `inject()` inside the store: navigation guards run
 * outside any component, so a store that could only resolve its dependencies
 * from an app context would work in the app and not in the router.
 *
 * `role` drives what the navigation shows and which routes resolve. It is not an
 * authorization decision — see `roleMayView` in the screen catalog, and I-3.
 */

import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import type {
  AuthClient,
  FederatedOutcome,
  FinishAccountInput,
  Role,
  Session,
  SignInInput,
  SignUpInput,
  SignUpOutcome
} from '@/lib/auth/contract'
import type { FederatedProvider } from '@/lib/auth/federated'
import { applyTheme, isThemePreference, resolveTheme, watchSystemTheme, type ThemeName, type ThemePreference } from '@/lib/theme'

export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated'

const SIGNED_OUT_THEME_KEY = 'match-point.theme.signed-out'

function readSignedOutPreference (): ThemePreference {
  try {
    const stored = globalThis.localStorage?.getItem(SIGNED_OUT_THEME_KEY)
    return isThemePreference(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

export const useSessionStore = defineStore('session', () => {
  let client: AuthClient | null = null
  let unwatchSystem: (() => void) | null = null

  const session = ref<Session | null>(null)
  const status = ref<SessionStatus>('unknown')
  /** Non-null while a sign-in, sign-up or reset is being awaited. */
  const pending = ref<null | 'sign-in' | 'sign-up' | 'reset' | 'sign-out'>(null)

  /**
   * Tracks the OS preference so `resolvedTheme` is reactive while the preference
   * is `'system'`. Never derived from the clock — see `lib/theme.ts`.
   */
  const systemPreference = ref<ThemeName>('ink-clinical')

  /**
   * The choice somebody made before signing in, kept in this browser.
   *
   * It used to be ignored: signed out, the switch on the sign-in and public pages
   * did nothing and the OS decided, so a person whose system is dark could not get
   * the light theme at all (reported 2026-09-26). A control that does nothing is
   * worse than a second place a preference lives, so it lives here until there is
   * an account — and once there is, the account's own preference wins.
   */
  const signedOutPreference = ref<ThemePreference>(readSignedOutPreference())

  const role = computed<Role | null>(() => session.value?.role ?? null)
  const isAuthenticated = computed(() => session.value !== null)
  const themePreference = computed<ThemePreference>(() => session.value?.themePreference ?? signedOutPreference.value)
  const resolvedTheme = computed<ThemeName>(() =>
    themePreference.value === 'system' ? systemPreference.value : themePreference.value
  )

  /** Where a signed-in user goes when they land somewhere they should not be. */
  const roleHomePath = computed(() => (isAuthenticated.value ? '/home' : '/'))

  function attach (next: AuthClient): void {
    client = next
  }

  function required (): AuthClient {
    if (!client) throw new Error('Session store has no AuthClient. Call attach() before use.')
    return client
  }

  /**
   * Applies the theme to the document and keeps following the OS while the
   * preference is `'system'`.
   *
   * Called from `bootstrap()` rather than at module scope so a test can drive the
   * store without a DOM being mutated behind its back.
   */
  function startThemeSync (root: HTMLElement): void {
    unwatchSystem?.()
    unwatchSystem = watchSystemTheme(theme => {
      systemPreference.value = theme
    })
    systemPreference.value = resolveTheme('system')
    watch(
      themePreference,
      preference => applyTheme(preference, root),
      { immediate: true }
    )
  }

  async function bootstrap (root?: HTMLElement): Promise<void> {
    session.value = await required().restore()
    status.value = session.value ? 'authenticated' : 'anonymous'
    if (root) startThemeSync(root)
  }

  async function signIn (input: SignInInput): Promise<Session> {
    pending.value = 'sign-in'
    try {
      const next = await required().signIn(input)
      session.value = next
      status.value = 'authenticated'
      return next
    } finally {
      pending.value = null
    }
  }

  /**
   * Signing up does NOT sign you in.
   *
   * It used to, because the fake could invent a session. A real account is
   * UNCONFIRMED until the emailed code is entered, so this returns the outcome
   * and leaves the store anonymous; the caller routes the user to confirmation.
   * Marking the store `authenticated` here would raise the whole application
   * shell around a session that does not exist, whose every request would 401.
   */
  async function signUp (input: SignUpInput): Promise<SignUpOutcome> {
    pending.value = 'sign-up'
    try {
      return await required().signUp(input)
    } finally {
      pending.value = null
    }
  }

  /** `ACC-02`'s second half. On success the account can sign in. */
  async function confirmSignUp (email: string, code: string): Promise<void> {
    pending.value = 'sign-up'
    try {
      await required().confirmSignUp(email, code)
    } finally {
      pending.value = null
    }
  }

  /** Ask for another code. See `ACC-02`'s second half. */
  async function resendConfirmationCode (email: string): Promise<void> {
    pending.value = 'sign-up'
    try {
      await required().resendConfirmationCode(email)
    } finally {
      pending.value = null
    }
  }

  async function requestPasswordReset (email: string): Promise<void> {
    pending.value = 'reset'
    try {
      await required().requestPasswordReset(email)
    } finally {
      pending.value = null
    }
  }

  /** `ACC-04`'s second half. On success the account signs in with the new password. */
  async function confirmPasswordReset (email: string, code: string, newPassword: string): Promise<void> {
    pending.value = 'reset'
    try {
      await required().confirmPasswordReset(email, code, newPassword)
    } finally {
      pending.value = null
    }
  }

  async function signOut (): Promise<void> {
    pending.value = 'sign-out'
    try {
      await required().signOut()
      session.value = null
      status.value = 'anonymous'
    } finally {
      pending.value = null
    }
  }

  /**
   * DESIGN.md section 5: the override is persisted **per account**, so it goes
   * through the auth client rather than into `localStorage` from here. The
   * Cognito client currently keys it in `localStorage` by user id — no endpoint
   * stores it yet (see the note in `lib/auth/cognito.ts`) — and when one exists
   * it follows the user to another machine with no UI change.
   */
  async function setThemePreference (preference: ThemePreference): Promise<void> {
    if (!session.value) {
      signedOutPreference.value = preference
      try {
        globalThis.localStorage?.setItem(SIGNED_OUT_THEME_KEY, preference)
      } catch {
        // Storage can be refused (private windows, blocked site data). The choice
        // still applies for this page; it just will not survive a reload.
      }
      return
    }
    session.value = await required().setThemePreference(preference)
  }

  /** Whether this build can send anybody to a provider at all (ADR 0012). */
  const canFederate = computed(() => client?.federatedSignIn != null)

  /**
   * Leaves for the provider. Nothing after this line of the caller runs in the
   * normal case: the browser navigates away, and `/auth/callback` picks it up.
   */
  async function federatedSignIn (provider: FederatedProvider, returnTo: string | null): Promise<void> {
    const start = required().federatedSignIn
    if (!start) throw new Error('This AuthClient has no federated sign-in.')
    pending.value = 'sign-in'
    try {
      await start(provider, returnTo)
    } catch (error) {
      pending.value = null
      throw error
    }
  }

  /** Whether this build has a staff pool to send anybody to (ADR 0016). */
  const canStaffSignIn = computed(() => client?.staffSignIn != null)

  /**
   * Leaves for Match Point's Entra tenant, on the staff pool. As for
   * `federatedSignIn`, the browser navigates away and `/auth/callback` finishes.
   */
  async function staffSignIn (returnTo: string | null): Promise<void> {
    const start = required().staffSignIn
    if (!start) throw new Error('This AuthClient has no staff sign-in.')
    pending.value = 'sign-in'
    try {
      await start(returnTo)
    } catch (error) {
      pending.value = null
      throw error
    }
  }

  /**
   * `/auth/callback`'s half. A `signed-in` outcome raises the shell; a
   * `needs-account` one leaves the store anonymous, because that token opens
   * nothing but the finish-account screen.
   */
  async function completeFederatedSignIn (query: Readonly<Record<string, string | null>>): Promise<FederatedOutcome> {
    pending.value = 'sign-in'
    try {
      const outcome = await required().completeFederatedSignIn(query)
      if (outcome.kind === 'signed-in') {
        session.value = outcome.session
        status.value = 'authenticated'
      }
      return outcome
    } finally {
      pending.value = null
    }
  }

  /** Creates the account and signs in with the refreshed session. */
  async function finishFederatedAccount (input: FinishAccountInput): Promise<Session> {
    pending.value = 'sign-up'
    try {
      const next = await required().finishFederatedAccount(input)
      session.value = next
      status.value = 'authenticated'
      return next
    } finally {
      pending.value = null
    }
  }

  /** Local development only; `impersonateRole` is `null` on a real client. */
  const canSwitchRole = computed(() => client?.impersonateRole != null)

  async function switchRole (next: Role): Promise<void> {
    const impersonate = required().impersonateRole
    if (!impersonate) throw new Error('This AuthClient cannot switch roles.')
    session.value = await impersonate(next)
    status.value = 'authenticated'
  }

  return {
    session,
    status,
    pending,
    role,
    isAuthenticated,
    themePreference,
    resolvedTheme,
    roleHomePath,
    canSwitchRole,
    canFederate,
    canStaffSignIn,
    attach,
    bootstrap,
    startThemeSync,
    signIn,
    federatedSignIn,
    staffSignIn,
    completeFederatedSignIn,
    finishFederatedAccount,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    requestPasswordReset,
    confirmPasswordReset,
    signOut,
    setThemePreference,
    switchRole
  }
})
